import React, {
  useEffect,
  useRef,
  FunctionComponent,
  useContext,
  useState,
  Fragment,
} from "react";
import { Icon } from "../utils/Icons";
import { FindAStoreContext, Place } from "../contexts/findAStore";
import { StateContext } from "../contexts/state";
import { abbrIsRapidCash } from "../utils/states";
import { Brands, Store } from "../types";

interface Props {
  getPlace: (location: Place) => void;
  brands: string[];
  store?: Store;
}

const SearchBar: FunctionComponent<Props> = React.memo((props: Props) => {
  const {
    state: { place, storesInView, queryString, map },
  } = useContext(FindAStoreContext);
  const { customerState } = useContext(StateContext);
  const [isRapidCash, setIsRapidCash] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const locateButtonRef = useRef<HTMLButtonElement>(null);

  const google = typeof window !== "undefined" ? window.google : (null as any);
  useEffect(() => {
    setIsRapidCash(abbrIsRapidCash(customerState));
  }, [customerState]);
  const updatePosition = (position: any) => {
    const latLng = new google.maps.LatLng(
      position.coords.latitude,
      position.coords.longitude
    );
    const location: Place = {
      geo: {
        latLng: latLng,
        latLngBounds: undefined,
      },
      addressComponents: undefined,
      source: "location",
    };
    props.getPlace(location);
  };

  const updateGeoMobile = () => {
    navigator.geolocation.getCurrentPosition(updatePosition, () =>
      alert(
        "Unable to use location services. Please check if you have location services enabled for this site or use the search box below."
      )
    );
  };

  const enableEnterKey = (input: HTMLDivElement) => {
    /* Store original event listener */
    const _addEventListener = input.addEventListener;

    const addEventListenerWrapper = (type: string, listener: EventListener) => {
      if (type === "keydown") {
        /* Store existing listener function */
        const _listener = listener;
        listener = ((event: KeyboardEvent) => {
          /* Simulate a 'down arrow' keypress if no address has been selected */
          const suggestionSelected =
            document.getElementsByClassName("pac-item-selected").length;
          if (event.key === "Enter" && suggestionSelected == 0) {
            const e = new KeyboardEvent("keydown", {
              key: "ArrowDown",
              code: "40",
            });
            Object.defineProperty(e, "keyCode", { value: 40 });
            _listener.apply(input, [e]);
          }
          _listener.apply(input, [event]);
        }) as EventListener;
      }
      _addEventListener.apply(input, [type, listener]);
    };
    input.addEventListener = addEventListenerWrapper;
  };

  const handleSearchButton = (input: HTMLInputElement) => {
    if (input.value) {
      input.focus();
      input.dispatchEvent(new KeyboardEvent("keydown", { key: "Enter" }));
      google.maps.event.trigger(input, "keydown", { keyCode: 13 });
      input.dispatchEvent(new KeyboardEvent("keydown", { key: "Enter" }));
      google.maps.event.trigger(input, "keydown", { keyCode: 13 });
    }
  };

  const checkQueryString = (input: HTMLInputElement) => {
    if (queryString && queryString.zip) {
      const searchTerm = queryString.zip;
      const zoom = 1;
      const lat = 1;
      const lng = 1;
      const center = new google.maps.LatLng(lat, lng);
      const mapConfig = Object.assign(
        {},
        {
          center: center,
          zoom: zoom,
        }
      );
      const map = new google.maps.Map(
        document.getElementById as any,
        mapConfig
      );
      if (map) {
        const service = new google.maps.places.PlacesService(map);
        service.findPlaceFromQuery(
          {
            query: searchTerm,
            fields: ["formatted_address", "geometry"],
          },
          (results: any, status: any) => {
            if (status !== google.maps.places.PlacesServiceStatus.OK) {
              //console.log('Error -- Find Place from Query: ' + status)
            }
            if (
              status === google.maps.places.PlacesServiceStatus.OK &&
              results[0].geometry
            ) {
              const lat = results[0].geometry?.location.lat();
              const lng = results[0].geometry?.location.lng();
              const place: Place = {
                geo: {
                  latLng: new google.maps.LatLng(lat, lng),
                  latLngBounds: results[0].geometry?.viewport,
                },
                addressComponents: undefined,
                source: "search",
              };
              input.value = results[0].formatted_address;
              props.getPlace(place);
            }
          }
        );
      }
    }
  };

  useEffect(() => {
    const options = {
      types: ["geocode"],
      componentRestrictions: {
        country: props.brands.includes(2) ? "ca" : "us",
      },
    };
    if (inputRef.current !== null) {
      const searchBox = new google.maps.places.Autocomplete(
        inputRef.current,
        options
      );
      searchBox.setFields(["geometry"]);
      const listenerId = searchBox?.addListener("place_changed", (() => {
        const placeResult = searchBox.getPlace();
        const lat = placeResult.geometry?.location.lat();
        const lng = placeResult.geometry?.location.lng();
        const place: Place = {
          geo: {
            latLng: new google.maps.LatLng(lat, lng),
            latLngBounds: placeResult.geometry?.viewport,
          },
          addressComponents: placeResult.address_components,
          source: "search",
        };
        props.getPlace(place);
      }) as EventListener);
      enableEnterKey(inputRef.current);
      return () => google.maps.event.removeListener(listenerId);
    }
  }, [inputRef.current, props.getPlace]);

  useEffect(() => {
    if (place == undefined && inputRef.current) {
      inputRef.current.value = "";
    }
  }, [place]);

  useEffect(() => {
    const input = document.getElementById("autocomplete");
    checkQueryString(input as HTMLInputElement);
  }, []);

  return (
    <div>
      <div
        sx={{
          height: "auto",
          maxHeight:
            (storesInView.length == 0 && !place && !props.store) || !map
              ? "350px"
              : "0px",
          transition: "max-height 500ms",
          overflow: "hidden",
          textAlign: "center",
          px: 2,
          width: "100%",
        }}
      >
        <div>
          <h1
            sx={{
              color: "brand.black",
              fontFamily: "heading",
              fontWeight: "700",
              fontSize: ["1.625rem", null, "2.875rem"],
              lineHeight: 1.2,
              mb: [3, null, 4],
            }}
          >
            Find a Cash Money Store
          </h1>
        </div>
      </div>
      <div
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          justifyContent: "center",
          flexShrink: 0,
          px: ["12px", null, null, "0px"],
        }}
      >
        <div
          sx={{
            display: "flex",
            width: [
              "100%",
              null,
              null,
              storesInView.length <= 0 ? "350px" : "100%",
            ],
            justifyContent: "space-between",
            boxShadow:
              storesInView.length <= 0 ? "0px 3px 6px #00000029" : null,
            borderRadius:
              storesInView.length <= 0 ? [0, null, null, "9px"] : "0px",
            fontSize: "1.125em",
            p: 1,
            mb: 1,
            mt: "0px",
          }}
        >
          <label
            htmlFor="autocomplete"
            sx={{
              height: "0px",
              width: "0px",
              color: "rgba(255,255,255,0)",
            }}
          >
            Search for your location
          </label>
          <input
            name="search"
            ref={inputRef}
            id="autocomplete"
            placeholder="Enter Postal Code or City"
            sx={{
              width: "calc(100% - 64px)",
              borderRadius: "50px 0px 0px 50px",
              lineHeight: 1.5,
              backgroundClip: "padding-box",
              color: "#000",
              backgroundColor: storesInView.length > 0 ? "#f4f4f4" : null,
              fontSize: "1em",
              textAlign: "left",
              flexGrow: 10,
              padding: "10px 0px 10px 15px",
              ":focus": {
                outline: "none",
              },
              ".pac-container": {
                marginTop: "-200px !important",
              },
            }}
          />
          <button
            id="searchButton"
            ref={searchButtonRef}
            tabIndex={0}
            aria-label="Search"
            onClick={() =>
              inputRef.current ? handleSearchButton(inputRef.current) : void 0
            }
            sx={{
              lineHeight: 1.5,
              px: "5px",
              backgroundColor: storesInView.length > 0 ? "#f4f4f4" : null,
              color: storesInView.length > 0 && "placeholder",
              border: storesInView.length > 0 && "1px solid transparent",
              fontSize: storesInView.length > 0 && "26px",
              borderRadius:
                storesInView.length > 0 ? "0px 50px 50px 0px" : null,
              paddingRight: storesInView.length > 0 ? "15px" : null,
              paddingLeft: storesInView.length > 0 ? "5px" : null,
              ":focus": {
                outline: "none",
              },
            }}
          >
            <Icon
              alt="Search"
              name="search"
              height={26}
              width={26}
              fill={"#000"}
              sx={{
                m: 1,
              }}
            />
          </button>
          <button
            ref={locateButtonRef}
            tabIndex={0}
            aria-label="Find stores near me"
            onClick={() => updateGeoMobile()}
            sx={{
              lineHeight: 1.5,
              backgroundColor: "#fff",
              color: "placeholder",
              border: "1px solid transparent",
              fontSize: "26px",
              paddingLeft:
                storesInView.length > 0 || place || props.store ? "5px" : "0px",
              width:
                storesInView.length > 0 || place || props.store
                  ? "45px"
                  : "0px",
              overflow: "hidden",
              transition: "width 500ms",
            }}
          >
            <Icon
              alt="Use My Location"
              name="findMe"
              height={30}
              width={30}
              viewBox="0 0 342 342"
              fill={storesInView.length > 0 ? "brand.red" : "black"}
              sx={{
                m: 1,
              }}
            />
          </button>
        </div>

        <div>
          {storesInView.length <= 0 && (
            <p
              sx={{
                my: "14px",
                textAlign: "center",
              }}
            >
              OR
            </p>
          )}

          <button
            ref={locateButtonRef}
            tabIndex={0}
            aria-label="Find stores near me"
            onClick={() => updateGeoMobile()}
            sx={{
              variant: "buttons.base",
              color: "brand.red",
              textDecoration: "underline",
              width: "100%",
              p: 0,
              maxWidth: "400px",
              letterSpacing: ".5px",
              maxHeight: "50px",
              fontSize: ["1rem", null, null, "1.125em"],
              mb: storesInView.length > 0 ? 5 : "0px",
              mt: storesInView.length > 0 ? 3 : "0px",
              display: storesInView.length > 0 ? "none" : "block",
            }}
          >
            Use my Current Location
            <Icon
              alt="Use My Location"
              name="findMe"
              height={20}
              width={20}
              viewBox="0 0 342 342"
              fill="brand.red"
              sx={{
                ml: 2,
                mb: 1,
              }}
            />
          </button>
        </div>
      </div>
    </div>
  );
});

export default SearchBar;
